import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useBundle } from '@amzn/react-arb-tools';
import React, { useEffect, useMemo, useState } from 'react';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { Loading } from 'src/common/loaders/Loading';
import { StickyContainer, Link, SectionDivider } from 'src/common/styled';
import { usePreviewOrder, useOffRampToAmazon } from 'src/hooks';
import { useBagStore, usePaymentStore, useCreatorAttributionStore } from 'src/store';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { isFixablePreviewError, PreviewOrderStatus } from 'src/utils/previewOrderErrorUtils';
import { createPreviewOrderRequest } from 'src/utils/previewOrderUtils';
import { ChargeBreakdown, DeliveryDetails, EstimatedDelivery, PaymentDetails, PreviewPageAlerts } from './components';
import { AddToCartButton } from 'src/common/buttons/addToCart';
import { PurchaseButton } from 'src/common/buttons/purchase';
import { useAlert } from 'src/common/alert/AlertContext';
/**
 * Converted from: https://code.amazon.com/packages/HandshakePainterComponents/blobs/mainline/--/src/kata/templates/checkout/PreviewOrders.kata
 *
 * TODO:
 *   - Graceful handling of preview load without bag contents.
 *     May want to add ASIN to query params in DetailPage and direct them back.
 */
export const PreviewOrderPage = () => {
    var _a;
    const [bundle, isBundleLoading] = useBundle('pages.previewOrderPage');
    const [isPurchaseButtonDisabled, _setIsPurchaseButtonDisabled] = useState(false);
    const [isCartButtonDisabled, _setIsCartButtonDisabled] = useState(false);
    const [showBoxShadow, setShowBoxShadow] = useState(false);
    const redirectUrls = useRetailRedirectUrls([
        RetailRoutes.CONDITIONS_OF_USE,
        RetailRoutes.PAYMENT_SELECTION_WALLET,
        RetailRoutes.PRIVACY_NOTICE,
    ]);
    const { clearAlert } = useAlert();
    // Get relevant session storage contents for preview order request
    const { listBag } = useBagStore();
    const { selectedPaymentMethodId } = usePaymentStore();
    const { getCreatorAttributionToken } = useCreatorAttributionStore();
    const bagContents = listBag();
    const pdpAsin = bagContents[0].asin;
    const isPrime = !!((_a = bagContents[0]) === null || _a === void 0 ? void 0 : _a.isPrime);
    const creatorAttributionToken = getCreatorAttributionToken();
    const previewOrderRequest = createPreviewOrderRequest(bagContents, selectedPaymentMethodId, creatorAttributionToken);
    const { data: orderPreview, isLoading, isError, refetch, isFetching } = usePreviewOrder(previewOrderRequest);
    const offRampToAmazon = useOffRampToAmazon();
    const previewOrderStatus = useMemo(() => {
        if (isError ||
            ((orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason) &&
                (!orderPreview.errorReason.code || !isFixablePreviewError(orderPreview.errorReason.code)))) {
            return PreviewOrderStatus.UNFIXABLE_ERROR;
        }
        else if (orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason) {
            return PreviewOrderStatus.FIXABLE_ERROR;
        }
        else {
            return PreviewOrderStatus.SUCCESS;
        }
    }, [isError, orderPreview]);
    const openExternalLink = (e) => {
        e.preventDefault();
        const url = e.currentTarget.href;
        offRampToAmazon(url, ReportActionSource.PreviewOrderPage);
    };
    /**
     * We listen to the window but need to set the `useCapture` option to true
     * https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#usecapture
     *
     * Stack overflow: https://stackoverflow.com/questions/36207398/not-getting-callback-after-adding-an-event-listener-for-scroll-event-in-react-js/65431632
     */
    useEffect(() => {
        const handleScrollEvent = (e) => {
            const target = e.target;
            setShowBoxShadow(target.scrollTop > 48);
        };
        window.addEventListener('scroll', handleScrollEvent, true);
        return () => {
            window.removeEventListener('scroll', handleScrollEvent);
        };
    }, []);
    useEffect(() => {
        if (isFetching) {
            clearAlert();
        }
    }, [isFetching]);
    if (isBundleLoading || isLoading || isFetching) {
        return _jsx(Loading, {});
    }
    return (_jsxs(Column, { spacing: "none", "data-testid": "preview-order-page", children: [_jsxs(StickyContainer, { spacingInset: "350 350 300 350", spacing: "300", showShadow: showBoxShadow, children: [_jsx(PreviewPageAlerts, { previewOrderStatus: previewOrderStatus, errorReason: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason, offerDetailsChanged: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.offerDetailsChanged }), previewOrderStatus === PreviewOrderStatus.SUCCESS && (_jsx(Text, { "data-testid": "legal-note", type: "b100", children: bundle.formatMessage('legalNote', {
                            conditionsLink: (chunks) => (_jsx(Link, { underline: true, href: redirectUrls.CONDITIONS_OF_USE, onClick: openExternalLink, "data-testid": "conditions-link", children: chunks }, "conditions-link")),
                            privacyLink: (chunks) => (_jsx(Link, { underline: true, href: redirectUrls.PRIVACY_NOTICE, onClick: openExternalLink, "data-testid": "privacy-link", children: chunks }, "privacy-link")),
                        }) })), _jsxs(Column, { spacing: "300", "data-testid": "cta-block", children: [_jsx(PurchaseButton, { isDisabled: isPurchaseButtonDisabled, previewOrderStatus: previewOrderStatus, refetch: refetch, asin: pdpAsin, purchaseId: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.purchaseId, isPrime: isPrime }), _jsx(AddToCartButton, { isDisabled: isCartButtonDisabled, isPrime: isPrime })] })] }), _jsx(Row, { spacingInset: "none 350 400 350", children: _jsx(Text, { type: "b100", children: bundle.formatMessage('changePaymentMethodAndAddress', {
                        accountLink: (chunks) => (_jsx(Link, { underline: true, href: redirectUrls.PAYMENT_SELECTION_WALLET, onClick: openExternalLink, "data-testid": "change-payment-method-and-address", children: chunks }, "account-link")),
                    }) }) }), !isError && (_jsxs(_Fragment, { children: [_jsx(SectionDivider, {}), _jsx(ChargeBreakdown, { charges: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.charges })] })), _jsx(SectionDivider, {}), _jsx(PaymentDetails, { errorReason: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason, paymentMethod: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.paymentMethod }), _jsx(SectionDivider, {}), _jsx(DeliveryDetails, { errorReason: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason, shippingAddress: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.shippingAddress }), _jsx(SectionDivider, {}), _jsx(EstimatedDelivery, { orders: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.orders, bagContents: bagContents[0] })] }));
};
